import type { ScopeEventOption } from './ScopeEditor';

/**
 * Turning a room's schedule into what a scope editor needs, once rather than in each console.
 *
 * The input is declared structurally — the fields a tournament has that this cares about — so
 * both apps pass their tournament list straight in without `@brush/ui` taking a dependency on
 * the API client, the same way `Rundown` and the preview pane take their shapes.
 */
export interface ScopeEventSource {
  id: string;
  /** The room's own reference for the event. This is what an operator reads and searches by. */
  internalId: string;
  name: string;
  /** `yyyy-MM-dd`. */
  date: string;
  tags: string[];
}

/**
 * Newest first, and labelled the way the room refers to an event.
 *
 * A tournament's `id` is a generated GUID that names nothing to anybody, which is exactly why
 * `internalId` exists — so the override lists show that and the name, and search matches either.
 */
export function scopeEventsFrom(
  tournaments: readonly ScopeEventSource[],
): ScopeEventOption[] {
  return [...tournaments]
    .sort((a, b) => b.date.localeCompare(a.date) || a.internalId.localeCompare(b.internalId))
    .map((tournament) => ({
      id: tournament.id,
      label: `${tournament.internalId} · ${tournament.name}`,
      date: tournament.date,
      tags: tournament.tags,
    }));
}

/**
 * Every tag the scope could sensibly filter on: the platform's vocabulary plus whatever this room
 * has actually used.
 *
 * Alphabetical, deliberately — the tag pickers that order by use are the ones where an operator
 * reaches for a usual tag, and this is the other kind, where they are looking for a particular
 * one. It matches how the tournaments filter bar is ordered for the same reason.
 *
 * Case-insensitive de-duplication, matching how SQL Server compares them: a room that typed
 * `nlh` where the platform defines `NLH` must not see the tag twice.
 */
export function tagVocabulary(
  tournaments: readonly { tags: string[] }[],
  platformTags: readonly string[] = [],
): string[] {
  const seen = new Map<string, string>();

  for (const tag of platformTags) seen.set(tag.toLowerCase(), tag);
  for (const tournament of tournaments) {
    for (const tag of tournament.tags) {
      if (!seen.has(tag.toLowerCase())) seen.set(tag.toLowerCase(), tag);
    }
  }

  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * `tagVocabulary` from the room's tag counts (`GET /api/rooms/{roomId}/tournament-tags`) instead of a tournament
 * list, which is what the scope editors read since known gap 35: the counts cover every tournament the room holds,
 * where a fetched list stopped at the newest 200. The counts are by exact spelling, so a room carrying `MIXED`
 * and `Mixed` sends both; this keeps one, the platform's spelling first and then the first the counts list.
 */
export function tagVocabularyFromUsage(
  usage: readonly { tag: string; tournaments: number }[],
  platformTags: readonly string[] = [],
): string[] {
  return tagVocabulary(usage.map((u) => ({ tags: [u.tag] })), platformTags);
}

/**
 * What an event picker says when it lists nothing (known gap 112), or null when it lists something. The room's
 * events are read after the screen opens, so "not read yet" and "could not be read" are states of their own —
 * telling an operator the room has no events, while they are on their way, sends them to record tournaments the
 * room already holds.
 */
export function eventListNotice(events: readonly ScopeEventOption[] | null, failed = false): string | null {
  if (events === null) return failed ? "This room's events couldn't be read." : "Reading this room's events…";
  return events.length === 0 ? 'This room has no events to choose from yet.' : null;
}
